import React, { useContext, useState } from 'react';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import 'moment/locale/nl';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/ja';
import 'moment/locale/ko';
import 'moment/locale/pt';
import 'moment/locale/zh-cn';

import { useToggle } from '@react-hookz/web';
import type { AxiosInstance, AxiosError } from 'axios';
import type { TFunction } from 'i18next';
import { notification } from 'antd';

import type { ErrorDescription, UserDetail } from 'src/api-sdk';
import { ProfilesApi, TokenApi } from 'src/api-sdk';

import { useAPI } from './use-api';
import { QUERIES } from './globals';

//
// AUTH CONTEXT INTERFACE
//
interface AuthContextInterface {
  user?: UserDetail;
  setRefreshToken: React.Dispatch<React.SetStateAction<string | undefined>>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: (token: string) => Promise<{ access?: string }>;
  getUser: () => Promise<UserDetail>;
  isAuthenticationInitialized: boolean;
  refreshToken?: string;
  apiKey?: string;
  originalRequestUrl: string;
}

const authContext = React.createContext<AuthContextInterface>(
  {} as AuthContextInterface
);

interface ProvideAuthProps {
  children: React.ReactNode;
}

//
// PROVIDER & HOOK EXPORTS
//
export function ProvideAuth({ children }: ProvideAuthProps): JSX.Element {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = (): AuthContextInterface => useContext(authContext);

//
// SINGLE-FLIGHT REFRESH
//
// Wraps a refresh call so concurrent invocations share one in-flight
// request instead of each starting its own refresh-token rotation. Only
// the shared attempt's failure runs `onFailure` (logout + redirect), once,
// regardless of how many callers are waiting on it.
export function singleFlightRefresh(
  stateRef: React.MutableRefObject<Promise<{ access?: string }> | null>,
  doRefresh: (token: string) => Promise<{ access?: string }>,
  onFailure: (error: unknown) => void
): (token: string) => Promise<{ access?: string }> {
  return (token: string) => {
    if (!stateRef.current) {
      const promise = doRefresh(token);
      promise.catch(onFailure);
      stateRef.current = promise.finally(() => {
        stateRef.current = null;
      });
    }
    return stateRef.current;
  };
}

//
// API ERROR INTERCEPTOR (using async/await)
//
export function createApiErrorInterceptor({
  t,
  logout,
  refresh,
  refreshToken,
  api,
  router,
  setOriginalRequestUrl,
}: {
  t: TFunction;
  logout: () => void;
  refresh: (token: string) => Promise<{ access?: string }>;
  refreshToken?: string;
  api: AxiosInstance;
  router: ReturnType<typeof useRouter>;
  setOriginalRequestUrl: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
}): (error: AxiosError) => Promise<any> {
  return async (error: AxiosError): Promise<any> => {
    // If there's no response, just pass the error along.
    if (!error.response) {
      throw error;
    }

    const url: string | undefined =
      error.request.url || error.request.responseURL;

    const data = error.response.data as ErrorDescription;

    // Incorrect credentials => log out and redirect to login.
    if (
      data &&
      (data.code === 'authentication_failed' ||
        data.code === 'account_inactive' ||
        data.code === 'user_not_found') &&
      refreshToken
    ) {
      console.log('Incorrect credentials, logging out.');
      if (!isUnRestrictedRoute(router.pathname)) {
        setOriginalRequestUrl(window.location.pathname);
      }
      logout();
      await router.push('/login');
      return Promise.reject({ ...error, handled: true });
    }

    // No Access Token provided => try to refresh.
    if (data && data.code === 'not_authenticated') {
      console.log('Access Token not provided, refreshing');
      if (url && url.includes('/token/refresh/')) {
        console.info(
          'Token expired and refresh failed, redirecting to login (not authenticated).'
        );
        if (!isUnRestrictedRoute(router.pathname)) {
          setOriginalRequestUrl(window.location.pathname);
        }
        logout();
        await router.push('/login');
        return Promise.reject({ ...error, handled: true });
      }

      if (refreshToken) {
        try {
          await refresh(refreshToken);
          console.log('Refresh succeeded, continuing request');
          return api.request(error.config!);
        } catch (refreshError) {
          // logout() and the /login redirect already happened once, shared
          // across every caller of this refresh attempt — see refresh().
          return Promise.reject({ ...error, handled: true });
        }
      } else {
        console.log('No refresh token, redirecting to login');
        if (!isUnRestrictedRoute(router.pathname)) {
          setOriginalRequestUrl(window.location.pathname);
        }
        logout();
        await router.push('/login');
        return Promise.reject({ ...error, handled: true });
      }
    }

    // Access Token has expired => try to refresh.
    // @ts-ignore
    if (data && data.code === 'token_not_valid') {
      if (url && url.includes('/token/refresh/')) {
        console.info(
          'Token expired and refresh failed, redirecting to login (not valid).'
        );

        if (!isUnRestrictedRoute(router.pathname)) {
          console.log('Redirecting to', window.location.pathname);
          setOriginalRequestUrl(window.location.pathname);
        }
        logout();
        await router.push('/login');
        return Promise.reject({ ...error, handled: true });
      } else if (refreshToken) {
        console.info('Token expired, trying to refresh using refresh token');
        try {
          const { access } = await refresh(refreshToken as string);
          console.log(
            'Token refreshed, retrying request to backend using new Access Token.'
          );
          error.config!.headers['Authorization'] = `Bearer ${access}`;
          return api.request(error.config!);
        } catch (refreshError) {
          console.info('Token refresh failed, logging out.');
          // logout() and the /login redirect already happened once, shared
          // across every caller of this refresh attempt — see refresh().
          return Promise.reject({ ...error, handled: true });
        }
      } else {
        console.info('Token expired, but no refresh token found, logging out.');
        if (!isUnRestrictedRoute(router.pathname)) {
          console.log('Redirecting to', window.location.pathname);
          setOriginalRequestUrl(window.location.pathname);
        }
        logout();
        await router.push('/login');
        return Promise.reject({ ...error, handled: true });
      }
    }

    // Handle specific input errors.
    if (data && data.description === 'Invalid input.') {
      data.title = t('Invalid Input.');
      data.description = t(
        'Something is incorrectly inputted, check your input and contact support if persistent.'
      );
    }

    if (
      data &&
      data.title &&
      data.description &&
      !(error.config as any)?.skipNotification
    ) {
      notification.error({
        message: data.title,
        description: data.description,
      });
    }

    // use-api's response interceptor already reports this to Bugsnag.
    throw error;
  };
}

//
// SESSION INTERCEPTOR HOOK
//
export function useSessionInterceptor({
  t,
  logout,
  refresh,
  refreshToken,
  api,
  setOriginalRequestUrl,
}: {
  t: TFunction;
  logout: () => void;
  refresh: (token: string) => Promise<{ access?: string }>;
  refreshToken?: string;
  api: AxiosInstance;
  setOriginalRequestUrl: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
}): void {
  const router = useRouter();

  React.useEffect(() => {
    console.debug('Adding interceptor for keeping track of session.');
    const errorInterceptor = createApiErrorInterceptor({
      t,
      logout,
      refresh,
      refreshToken,
      api,
      router,
      setOriginalRequestUrl,
    });
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      errorInterceptor
    );

    // Cleanup: eject the interceptor.
    return () => {
      api.interceptors.response.eject(interceptorId);
    };
  }, [t, logout, refresh, refreshToken, api, router, setOriginalRequestUrl]);
}

//
// PROVIDER HOOK: USEPROVIDEAUTH (using async/await)
//
function useProvideAuth(): AuthContextInterface {
  const router = useRouter();
  const { api, configuration, setAuthorization, authHeaderSet } = useAPI();
  const { t } = useTranslation('common');

  const [isAuthenticationInitialized, setIsAuthenticationInitialized] =
    useToggle(false);
  const [apiKey, setApiKey] = useState<string | undefined>();
  const [refreshToken, setRefreshToken] = useState<string | undefined>();
  const [originalRequestUrl, setOriginalRequestUrl] = useState<
    string | undefined
  >(undefined);

  /**
   * Get user details.
   */
  const getUser = React.useCallback(async (): Promise<UserDetail> => {
    const response = await new ProfilesApi(
      configuration,
      undefined,
      api
    ).profilesUserDetailsRetrieve();
    return response.data;
  }, [api, configuration]);

  const { data: user } = useQuery({
    queryKey: [QUERIES.USER_DETAILS],
    queryFn: getUser,
    enabled: authHeaderSet,
  });

  /**
   * Login a user.
   */
  const login = React.useCallback(
    async (username: string, password: string): Promise<void> => {
      const response = await new TokenApi(
        configuration,
        undefined,
        api
      ).tokenCreate({
        tokenObtainPairRequest: { username, password },
      });
      setRefreshToken(response.data.refresh);
      setAuthorization(response.data.access);
      setCookie('authentication.refresh_token', response.data.refresh);
    },
    [api, configuration, setAuthorization]
  );

  /**
   * Logout the user and clear the current session.
   */
  const logout = React.useCallback((): void => {
    console.log('Logging out');
    setRefreshToken(undefined);
    deleteCookie('authentication.refresh_token');
    deleteCookie('authentication.api_key');
    setApiKey(undefined);
    setAuthorization(null);
    setIsAuthenticationInitialized(false);
  }, [setAuthorization, setIsAuthenticationInitialized]);

  /**
   * Refresh Access Token using the stored Refresh Token.
   */
  const doRefresh = React.useCallback(
    async (token: string): Promise<{ access?: string }> => {
      if (!token) {
        if (!isUnRestrictedRoute(router.pathname)) {
          console.log('Redirecting to', window.location.pathname);
          setOriginalRequestUrl(window.location.pathname);
          await router.push('/login');
        } else {
          throw new Error('Refresh token not supplied');
        }
      }
      try {
        const response = await new TokenApi(
          configuration,
          undefined,
          api
        ).tokenRefreshCreate({
          tokenRefreshRequest: { refresh: token },
        });
        if (response?.data?.refresh) {
          console.log('Setting new refresh token in cookie');
          setCookie('authentication.refresh_token', response.data.refresh);
          setRefreshToken(response.data.refresh);
        }
        setAuthorization(response.data.access, 'Bearer');
        return { access: response.data.access };
      } catch (error) {
        console.log('Refresh failed, clearing auth state');
        console.log(error);
        // If the refresh-token request itself came back as a 401, the
        // response interceptor already ran logout()+redirect for it
        // (marked `handled`) — don't do it again here.
        throw error;
      }
    },
    [api, configuration, router, setAuthorization]
  );

  // Single-flight: concurrent refresh() calls (e.g. several 401s at once)
  // share one in-flight request instead of racing N refresh-token rotations.
  const refreshPromiseRef = React.useRef<Promise<{ access?: string }> | null>(
    null
  );

  const refresh = React.useCallback(
    (token: string): Promise<{ access?: string }> =>
      singleFlightRefresh(refreshPromiseRef, doRefresh, (error) => {
        if ((error as { handled?: boolean })?.handled) {
          // Already logged out and redirected once by the response
          // interceptor (the refresh request's own 401 was caught there).
          return;
        }
        if (!isUnRestrictedRoute(router.pathname)) {
          setOriginalRequestUrl(window.location.pathname);
          logout();
          router.push('/login');
        } else {
          logout();
        }
      })(token),
    [doRefresh, router, setOriginalRequestUrl, logout]
  );

  React.useEffect(() => {
    // Run the initialization code only once the router is ready.
    if (isAuthenticationInitialized || authHeaderSet || !router.isReady) {
      return;
    }

    (async () => {
      if (router.query.token) {
        setCookie('authentication.refresh_token', router.query.token as string);
        setRefreshToken(router.query.token as string);
        try {
          await refresh(router.query.token as string);
        } catch {
          if (!isUnRestrictedRoute(window.location.pathname)) {
            setOriginalRequestUrl(window.location.pathname);
            await router.push('/login');
          }
        }
      } else if (router.query.api_key) {
        console.log(`Received api key from URL`);
        const apiKeyFromQuery = router.query.api_key as string;
        setApiKey(apiKeyFromQuery);
        setCookie('authentication.api_key', apiKeyFromQuery);
        setAuthorization(apiKeyFromQuery, 'Token');
      } else if (getCookie('authentication.refresh_token')) {
        console.log(`Found refresh token in cookie`);
        try {
          await refresh(getCookie('authentication.refresh_token') as string);
          setRefreshToken(getCookie('authentication.refresh_token') as string);
        } catch {
          if (!isUnRestrictedRoute(window.location.pathname)) {
            setOriginalRequestUrl(window.location.pathname);
            await router.push('/login');
          }
        }
      } else if (getCookie('authentication.api_key')) {
        console.log(`Found api key in cookie`);
        const apiKeyFromCookie = getCookie('authentication.api_key') as string;
        setApiKey(apiKeyFromCookie);
        setAuthorization(apiKeyFromCookie, 'Token');
      } else {
        // Redirect to /login if not on an unrestricted route.
        if (!isUnRestrictedRoute(window.location.pathname)) {
          console.log('Setting original request URL', window.location.pathname);
          setOriginalRequestUrl(window.location.pathname);
          setAuthorization(null);

          console.log('Redirecting to login due to no credentials.');
          await router.push('/login');
        } else {
          console.log('No credentials found, stuck.');
        }
      }
      setIsAuthenticationInitialized(true);
    })();
  }, [
    refresh,
    router,
    setAuthorization,
    setOriginalRequestUrl,
    setIsAuthenticationInitialized,
    isAuthenticationInitialized,
    authHeaderSet,
  ]);

  useSessionInterceptor({
    t,
    logout,
    refresh,
    refreshToken,
    api,
    setOriginalRequestUrl,
  });

  return {
    user,
    setRefreshToken,
    login,
    logout,
    isAuthenticationInitialized,
    refresh,
    getUser,
    refreshToken,
    apiKey,
    originalRequestUrl: originalRequestUrl || '/',
  };
}

//
// ROUTE HELPERS
//
export const isUnauthenticatedRoute = (pathname: string): boolean => {
  return (
    pathname.includes('/login') ||
    pathname.includes('/account/password-reset') ||
    pathname.includes('/invitations')
  );
};

export const isUnRestrictedRoute = (pathname: string): boolean => {
  return (
    pathname.includes('/_error') ||
    isUnauthenticatedRoute(pathname) ||
    (pathname.startsWith('/articles/') && pathname !== '/articles/') ||
    pathname.includes('/logout') ||
    pathname.includes('/alerts/feedback')
  );
};
